import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/i18n";

/**
 * Floating "Speaking..." pill with a live audio waveform (ChatGPT-style).
 * Rendered while TTS audio is playing. Reads real-time frequency data from
 * the shared AnalyserNode ref and animates bars via requestAnimationFrame.
 */
export const SpeakingWaveform = ({ isSpeaking, analyserRef, onStop }) => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isSpeaking) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    const BARS = 28;
    let phase = 0; // Fallback animation phase when analyser not ready

    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      const analyser = analyserRef?.current;
      let data;
      if (analyser) {
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(buf);
        data = buf;
      }

      const barWidth = (w - (BARS - 1) * 3) / BARS;
      const centerY = h / 2;
      phase += 0.15;

      for (let i = 0; i < BARS; i++) {
        let amp;
        if (data && data.length) {
          // Sample from lower/mid frequencies (voice)
          const idx = Math.floor((i / BARS) * (data.length * 0.6));
          amp = data[idx] / 255;
        } else {
          // Fallback: pleasant sine animation while analyser initializes
          amp = 0.3 + 0.3 * Math.abs(Math.sin(phase + i * 0.35));
        }
        const barH = Math.max(3, amp * (h * 0.85));
        const x = i * (barWidth + 3);
        const y = centerY - barH / 2;

        // Gradient: purple → pink
        const gradient = ctx.createLinearGradient(0, y, 0, y + barH);
        gradient.addColorStop(0, "#a78bfa");
        gradient.addColorStop(1, "#ec4899");
        ctx.fillStyle = gradient;

        // Rounded bars
        const r = barWidth / 2;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + barWidth - r, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + r);
        ctx.lineTo(x + barWidth, y + barH - r);
        ctx.quadraticCurveTo(x + barWidth, y + barH, x + barWidth - r, y + barH);
        ctx.lineTo(x + r, y + barH);
        ctx.quadraticCurveTo(x, y + barH, x, y + barH - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isSpeaking, analyserRef]);

  if (!isSpeaking) return null;

  return (
    <div
      data-testid="speaking-waveform"
      className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 pointer-events-auto"
    >
      <div className="flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-zinc-900/95 backdrop-blur-md border border-purple-500/40 shadow-xl shadow-purple-500/20">
        <span className="text-xs font-medium text-purple-200 whitespace-nowrap">
          {t("speaking")}
        </span>
        <canvas
          ref={canvasRef}
          className="w-40 h-8"
          data-testid="speaking-waveform-canvas"
        />
        <button
          onClick={onStop}
          data-testid="speaking-stop-btn"
          className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
          aria-label="stop speaking"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default SpeakingWaveform;
